import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Lock, Mail, Phone, Send, ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const detectChannel = (v) => {
  const s = (v || "").trim();
  if (s.includes("@")) return "email";
  if (s.startsWith("+") || /^\d[\d\s\-()]{6,}$/.test(s)) return "sms";
  return "email";
};

const toE164 = (v) => {
  const s = (v || "").trim();
  if (s.startsWith("+")) return s.replace(/[^\d+]/g, "");
  const digits = s.replace(/\D/g, "");
  return digits ? `+${digits}` : "";
};

export default function OperatorLoginModal({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { loginClient } = useAuth();

  const [step, setStep] = useState("identifier"); // identifier | code
  const [rawInput, setRawInput] = useState("");
  const [channel, setChannel] = useState("email");
  const [identifier, setIdentifier] = useState("");
  const [masked, setMasked] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setStep("identifier"); setRawInput(""); setIdentifier(""); setMasked("");
    setCode(""); setDevCode(""); setBusy(false);
  };

  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const sendCode = async (e) => {
    e?.preventDefault();
    if (!rawInput.trim()) { toast.error("Enter your work email or phone"); return; }
    const ch = detectChannel(rawInput);
    const ident = ch === "email" ? rawInput.trim().toLowerCase() : toE164(rawInput);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/otp/request`, { identifier: ident, channel: ch });
      setChannel(ch);
      setIdentifier(ident);
      setMasked(data.masked || "");
      if (data.dev_code) setDevCode(data.dev_code);
      setStep("code");
      toast.success(`Code sent to ${data.masked || ident}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send code");
    } finally { setBusy(false); }
  };

  const verifyAndLogin = async (e) => {
    e?.preventDefault();
    if (!/^\d{4,10}$/.test(code)) { toast.error("Enter the numeric code"); return; }
    setBusy(true);
    try {
      const data = await loginClient({ identifier, channel, code: code.trim() });
      toast.success(`Welcome back${data.name ? `, ${data.name.split(" ")[0]}` : ""}`);
      close(false);
      navigate("/order");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-sm" data-testid="client-login-modal">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-[var(--brand)]/10 text-[var(--brand)] flex items-center justify-center">
              <ShieldCheck size={16} />
            </div>
            <div>
              <DialogTitle className="text-lg">Client login</DialogTitle>
              <DialogDescription className="text-xs">
                For enrolled MPIRE clients. Enter the email or phone on file — we'll send a one-time code.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {step === "identifier" ? (
          <form onSubmit={sendCode} className="space-y-3" data-testid="client-identifier-form">
            <div>
              <Label className="text-xs">Email or phone</Label>
              <Input
                autoFocus
                value={rawInput}
                onChange={e => setRawInput(e.target.value)}
                placeholder="you@yourshop.com or +1 415 555 0100"
                data-testid="client-identifier-input"
              />
            </div>
            <Button type="submit" className="btn-brand w-full" disabled={busy} data-testid="client-send-code">
              <Send size={14} className="mr-2" /> {busy ? "Sending…" : "Send code"}
            </Button>
          </form>
        ) : (
          <form onSubmit={verifyAndLogin} className="space-y-3" data-testid="client-code-form">
            <p className="text-xs text-gray-600 leading-snug">
              Code sent to <span className="font-medium">{masked || identifier}</span>. Enter it below.
            </p>
            {devCode && (
              <div className="rounded-lg border-2 border-dashed border-amber-400 bg-amber-50 p-2.5 text-xs" data-testid="client-dev-code">
                Dev mode — code: <code className="font-mono font-bold text-sm">{devCode}</code>
              </div>
            )}
            <div>
              <Label className="text-xs">Auth code</Label>
              <Input
                autoFocus
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ""))}
                inputMode="numeric"
                maxLength={6}
                placeholder="123456"
                data-testid="client-code-input"
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="btn-brand flex-1" disabled={busy} data-testid="client-verify-submit">
                <Lock size={14} className="mr-2" /> {busy ? "Verifying…" : "Verify & continue"}
              </Button>
              <Button type="button" variant="outline" onClick={() => { setStep("identifier"); setCode(""); setDevCode(""); }} data-testid="client-change-identifier">
                Back
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
